import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import {
  PricingTabType,
  type Validity
} from 'Services/ProductManagement/Features/Product/productSlice'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import { useConfirm } from 'Util/hooks/useConfirm'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList } from '@mui/lab'
import { Divider, Tab, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { t } from 'ttag'

interface ProductPricingTabsProps {
  activePeriod: Validity | null
  handleSelectActivePeriod: (activePeriod: Validity) => void
  handleDeleteActivePeriod: (period: Validity | null) => void
  onPricingTabChange: (tabType: PricingTabType) => void
  selectedTab: PricingTabType
  children: React.ReactNode
  allowedTabs?: PricingTabType[]
}

export default function ProductPricingTabs({
  activePeriod,
  handleSelectActivePeriod,
  handleDeleteActivePeriod,
  onPricingTabChange,
  selectedTab,
  children,
  allowedTabs
}: Readonly<ProductPricingTabsProps>) {
  const { confirm } = useConfirm()
  const { confirmForUnsavedChanges } = useUnsavedChanges()

  const isTabAllowed = (tabType: PricingTabType) => {
    if (!allowedTabs) return true
    return allowedTabs.includes(tabType)
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: PricingTabType) => {
    confirmForUnsavedChanges(() => {
      onPricingTabChange(newValue)
    })
  }

  return (
    <Box>
      {activePeriod && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 2,
            pb: 1
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {activePeriod.title}
          </Typography>
          <Box>
            <DeleteButton
              sx={{ mr: 1 }}
              size="small"
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message:
                    selectedTab === PricingTabType.EXCHANGE_PERIOD
                      ? t`Are you sure you want to delete the exchange period plan?`
                      : t`Are you sure you want to delete the pricing plan?`,
                  handleConfirm: () => {
                    handleDeleteActivePeriod(activePeriod)
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
            <CustomButton
              onClick={() => handleSelectActivePeriod(activePeriod)}
              size="small"
              startIcon={<EditIcon />}
              variant="text"
            >{t`Edit`}</CustomButton>
          </Box>
        </Box>
      )}
      <TabContext value={selectedTab}>
        <TabList aria-label={t`Product tab list`} onChange={handleTabChange}>
          {isTabAllowed(PricingTabType.PRICINGS) && (
            <Tab label={t`Pricings`} value={PricingTabType.PRICINGS} />
          )}
          {isTabAllowed(PricingTabType.EXCHANGE_PERIOD) && (
            <Tab label={t`Exchange period`} value={PricingTabType.EXCHANGE_PERIOD} />
          )}
        </TabList>
        <Divider />
        {children}
      </TabContext>
    </Box>
  )
}
